import { REQUEST_ID_HEADER, expected, randomUUID } from '@aloft/toolkit-shared';

import { getMockedResponse } from '../../_mocks/getMockedResponse.js';
import { getBaseUrl } from './getBaseUrl.js';
import { ToolkitContext } from './toolkitContext.js';

export type ErrorWrapper<TError> = TError | { status: 'unknown'; payload: string };

type ToolkitFetcherOptions<TBody, THeaders, TQueryParams, TPathParams> = {
  url: string;
  method: string;
  body?: TBody;
  headers?: THeaders;
  queryParams?: TQueryParams;
  pathParams?: TPathParams;
  signal?: AbortSignal;
} & ToolkitContext['fetcherOptions'];

export async function toolkitFetch<
  TData,
  TError,
  TBody extends object | FormData | undefined | null,
  THeaders extends object,
  TQueryParams extends object,
  TPathParams extends object,
>({
  url,
  method,
  body,
  headers,
  pathParams,
  queryParams,
  signal,
  accessTokenFn,
}: ToolkitFetcherOptions<TBody, THeaders, TQueryParams, TPathParams>): Promise<TData> {
  const override = getMockedResponse({
    url,
    method,
    body: body ?? {},
    pathParams: pathParams as Record<string, string> | undefined,
    queryParams,
  });
  if (override !== undefined) {
    return override as TData;
  }

  const requestHeaders: HeadersInit = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${await expected(accessTokenFn)()}`,
    ...headers,
  };

  requestHeaders[REQUEST_ID_HEADER] = randomUUID();

  /**
   * As the fetch API is being used, when multipart/form-data is specified
   * the Content-Type header must be deleted so that the browser can set
   * the correct boundary.
   * https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sending_files_using_a_formdata_object
   */
  if (requestHeaders['Content-Type']?.toLowerCase().includes('multipart/form-data')) {
    delete requestHeaders['Content-Type'];
  }

  const response = await window.fetch(
    `${getBaseUrl()}${resolveUrl(url, queryParams, pathParams ?? {})}`,
    {
      signal,
      method: method.toUpperCase(),
      body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
      headers: requestHeaders,
    },
  );

  // Handle non 2xx responses as errors
  if (!response.ok) {
    let error: ErrorWrapper<TError>;
    try {
      error = { status: response.status, payload: await response.json() } as TError;
    } catch (e) {
      error = {
        status: 'unknown' as const,
        payload: e instanceof Error ? `Unexpected error (${e.message})` : 'Unexpected error',
      };
    }

    throw error;
  }

  if (response.status === 204) {
    return undefined as TData;
  }

  try {
    if (response.headers.get('content-type')?.includes('json')) {
      return await response.json();
    } else {
      // if it is not a json response, assume it is a blob and cast it to TData
      return (await response.blob()) as unknown as TData;
    }
  } catch (e) {
    throw {
      status: 'unknown' as const,
      payload: e instanceof Error ? `Unexpected error (${e.message})` : 'Unexpected error',
    };
  }
}

const resolveUrl = (
  url: string,
  queryParams: Record<string, string> = {},
  pathParams: Record<string, string> = {},
) => {
  let query = new URLSearchParams(queryParams).toString();
  if (query) query = `?${query}`;
  //@ts-expect-error just gonna suppress this for now
  return url.replace(/\{\w*\}/g, (key) => pathParams[key.slice(1, -1)]) + query;
};
